import React, { useState } from 'react';
import { Modal } from '../Modal';
import { Input } from '../Input';
import { Dropdown } from '../Dropdown';
import { Toggle } from '../Toggle';
import { Button } from '../Button';
import styles from './IntegrationModal.module.css';

interface IntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (integration: IntegrationData) => void;
  toolName?: string;
}

export interface IntegrationData {
  project: string;
  globalIntegration: boolean;
  credentialType: string;
  alias: string;
  authFields: Record<string, string>;
}

const credentialTypeOptions = [
  { value: 'api_key', label: 'API Key' },
  { value: 'basic_auth', label: 'Basic Authentication' },
  { value: 'oauth2', label: 'OAuth 2.0' },
  { value: 'bearer_token', label: 'Bearer Token' },
];

const projectOptions = [
  { value: 'project1', label: 'Project 1' },
  { value: 'project2', label: 'Project 2' },
  { value: 'default', label: 'Default Project' },
];

export const IntegrationModal: React.FC<IntegrationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  toolName = 'Tool',
}) => {
  const [formData, setFormData] = useState<IntegrationData>({
    project: '',
    globalIntegration: false,
    credentialType: 'api_key',
    alias: '',
    authFields: {},
  });

  const handleFieldChange = (field: keyof IntegrationData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAuthFieldChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      authFields: {
        ...prev.authFields,
        [field]: value,
      },
    }));
  };

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  const renderAuthFields = () => {
    switch (formData.credentialType) {
      case 'api_key':
        return (
          <>
            <Input
              label="API Key"
              type="password"
              value={formData.authFields.apiKey || ''}
              onChange={(e) => handleAuthFieldChange('apiKey', e.target.value)}
              placeholder="Enter your API key"
            />
            <Input
              label="Base URL (optional)"
              value={formData.authFields.baseUrl || ''}
              onChange={(e) => handleAuthFieldChange('baseUrl', e.target.value)}
              placeholder="https://api.example.com"
            />
          </>
        );
      case 'basic_auth':
        return (
          <>
            <Input
              label="Username"
              value={formData.authFields.username || ''}
              onChange={(e) => handleAuthFieldChange('username', e.target.value)}
              placeholder="Enter username"
            />
            <Input
              label="Password"
              type="password"
              value={formData.authFields.password || ''}
              onChange={(e) => handleAuthFieldChange('password', e.target.value)}
              placeholder="Enter password"
            />
            <Input
              label="Base URL"
              value={formData.authFields.baseUrl || ''}
              onChange={(e) => handleAuthFieldChange('baseUrl', e.target.value)}
              placeholder="https://api.example.com"
            />
          </>
        );
      case 'oauth2':
        return (
          <>
            <Input
              label="Client ID"
              value={formData.authFields.clientId || ''}
              onChange={(e) => handleAuthFieldChange('clientId', e.target.value)}
              placeholder="Enter client ID"
            />
            <Input
              label="Client Secret"
              type="password"
              value={formData.authFields.clientSecret || ''}
              onChange={(e) => handleAuthFieldChange('clientSecret', e.target.value)}
              placeholder="Enter client secret"
            />
            <Input
              label="Authorization URL"
              value={formData.authFields.authUrl || ''}
              onChange={(e) => handleAuthFieldChange('authUrl', e.target.value)}
              placeholder="https://auth.example.com/authorize"
            />
            <Input
              label="Token URL"
              value={formData.authFields.tokenUrl || ''}
              onChange={(e) => handleAuthFieldChange('tokenUrl', e.target.value)}
              placeholder="https://auth.example.com/token"
            />
          </>
        );
      case 'bearer_token':
        return (
          <>
            <Input
              label="Bearer Token"
              type="password"
              value={formData.authFields.token || ''}
              onChange={(e) => handleAuthFieldChange('token', e.target.value)}
              placeholder="Enter bearer token"
            />
            <Input
              label="Base URL (optional)"
              value={formData.authFields.baseUrl || ''}
              onChange={(e) => handleAuthFieldChange('baseUrl', e.target.value)}
              placeholder="https://api.example.com"
            />
          </>
        );
      default:
        return null;
    }
  };

  const footer = (
    <>
      <Button variant="ghost" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSave}>
        Save Integration
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Create User Integration - ${toolName}`}
      footer={footer}
      size="md"
    >
      <div className={styles.form}>
        <Dropdown
          label="Project"
          options={projectOptions}
          value={formData.project}
          onChange={(value) => handleFieldChange('project', value)}
          placeholder="Select project"
        />

        <div className={styles.toggleRow}>
          <Toggle
            label="Global Integration"
            checked={formData.globalIntegration}
            onChange={(checked) => handleFieldChange('globalIntegration', checked)}
          />
          <span className={styles.toggleHint}>
            Enable to make this integration available across all projects
          </span>
        </div>

        <Dropdown
          label="Credential Type"
          options={credentialTypeOptions}
          value={formData.credentialType}
          onChange={(value) => handleFieldChange('credentialType', value)}
        />

        <Input
          label="Alias"
          value={formData.alias}
          onChange={(e) => handleFieldChange('alias', e.target.value)}
          placeholder="Enter an alias for this integration"
          helperText="A friendly name to identify this integration"
        />

        <div className={styles.authSection}>
          <h4 className={styles.authTitle}>Authentication</h4>
          <div className={styles.authFields}>
            {renderAuthFields()}
          </div>
        </div>
      </div>
    </Modal>
  );
};
